import * as yup from 'yup';
import { useTranslation } from 'react-i18next';
import Group from '../../../../components/form/elements/group';
import { useEffect, useMemo } from 'react';
import {
  makeButton,
  product,
  warehouse,
  partyNumber,
  renderWarehousePartyNumber,
} from './form-parts';
import { useService } from '../../../../services/services';
import { useParams } from 'react-router-dom';
import Select from '../../../../components/form/elements/select';
import { faPlus } from '@fortawesome/pro-regular-svg-icons';
import Decimal from '../../../../components/form/elements/decimal';
import Watch from '../../../../components/form/elements/watch';
import ClassifierGroup from '../../../../enums/classifier-group';
import Classifier from '../../../../enums/classifier';
import ClassifierValue from '../../../../interfaces/classifierValue';
import { map } from 'rxjs/operators';
import ObtainTypes from '../../../../enums/obtain-types';
import { useWarehouseContext } from './warehouse-context';
import {
  handlePrice,
  handleSum,
  handleVatRate,
  handleVat,
  handleSumWithVat,
  handlePackagingSize,
  handlePackagingAmount,
  handleAmount,
  handlePackagingUnit,
} from './use-deal-form-calculations';
import Amount from '../../../../components/form/elements/amount';
import toFixed from 'accounting-js/lib/toFixed.js';
import Date from '../../../../components/form/elements/date';
import { FormComponent } from '../../../../components/form';
import DealType from '../../../../enums/deal-type';
import asyncCategories from './async-categories';
import Text from '../../../../components/form/elements/text';
import css from './drawer-form.module.css';
import { setDealProductPartyNumbers } from './use-deal-party-numbers';

let exceptionClassifiers = [];

const additionalParametersKeys = ['PLANT_PROTECTION_PRODUCTS', 'MEDICATION'];

let calculationValues = {
  sum: '',
  price: '',
  vat: '',
  sum_with_vat: '',
  amount: '',
  vat_rate: '',
};

export function useDealForm(
  isProductObtainRequired,
  useCalculator,
  isProductObtainClassifiersRequired = false,
  formMethods,
  warehouseId,
  originalFormValues = null,
  productCreatable = true,
  keyValue = null
) {
  const { t } = useTranslation();
  const {
    farm,
    isOpen,
    exceptionProducts,
    setExceptionProducts,
    formValues,
    setOpenModal,
  } = useWarehouseContext();
  const { farmId, dealType } = useParams<{
    farmId: string;
    dealType: DealType;
  }>();

  const { getValues: getValuesMainForm } = formMethods; // main form

  const { classifierService, dealService, productService, warehouseService } =
    useService();

  useEffect(() => {
    if (isOpen) {
      calculationValues = {
        sum: '',
        price: '',
        vat: '',
        sum_with_vat: '',
        amount: '',
        vat_rate: '',
      };
    }
  }, [isOpen]);

  useEffect(() => {
    if (!exceptionClassifiers.length && farmId) {
      const subscribe = dealService
        .additionalAmountParameterProducts(farmId)
        .subscribe((classifier) => {
          exceptionClassifiers = classifier;
        });

      return () => subscribe.unsubscribe();
    }
  }, [dealService, farmId]);

  useEffect(() => {
    if (Object.keys(exceptionClassifiers).length > 0) {
      const classifier =
        exceptionClassifiers[formValues?.product_type_id?.value] ||
        exceptionClassifiers[formValues?.product_category_id?.value];

      if (!!classifier) {
        return setExceptionProducts(classifier);
      }
    }
  }, [formValues, setExceptionProducts]);

  const handleSpecialProducts = (value, key) => {
    let classifier = exceptionClassifiers[value?.value];

    switch (key) {
      case 'category':
        if (classifier && exceptionProducts === '') {
          return setExceptionProducts(classifier);
        }
        break;
      case 'type':
        if (classifier) {
          return setExceptionProducts(classifier);
        } else {
          classifier = exceptionClassifiers[value?.parent?.value];

          if (classifier) {
            return setExceptionProducts(classifier);
          }
        }
        break;
      case 'product':
        classifier = exceptionClassifiers[value?.type?.value];

        if (classifier) {
          return setExceptionProducts(classifier);
        }
        classifier = exceptionClassifiers[value?.type?.parent?.value];

        if (classifier) {
          return setExceptionProducts(classifier);
        }
        break;
    }

    setExceptionProducts('');
  };

  const newPartyNumberList = [
    DealType.sold,
    DealType.spent,
    DealType.self_consumed,
    DealType.moved,
  ];

  const newWarehouseList = [
    DealType.purchased,
    DealType.produced,
    DealType.recycled,
  ];

  return useMemo<FormComponent[]>(
    () =>
      [
        Group(
          [
            isProductObtainRequired
              ? Select(
                  'product_obtain_type_id',
                  {
                    options: [
                      {
                        label: t(`classifiers.${ObtainTypes.produced}`),
                        value: ObtainTypes.produced,
                      },
                      {
                        label: t(`classifiers.${ObtainTypes.purchased}`),
                        value: ObtainTypes.purchased,
                      },
                      {
                        label: t(`classifiers.${ObtainTypes.recycle_gained}`),
                        value: ObtainTypes.recycle_gained,
                      },
                    ],
                    label: 'pages.warehouse.deals.product_obtain_type',
                  },
                  true
                )
              : null,
            ...product(
              t,
              dealService,
              productService,
              farmId,
              dealType,
              productCreatable,
              isProductObtainClassifiersRequired,
              true,
              '',
              handleSpecialProducts,
              getValuesMainForm('subtype'),
              originalFormValues,
              keyValue
            ),

            exceptionProducts === 'MEDICATION' &&
            dealType === DealType.purchased
              ? Select(
                  'packaging_type_id',
                  {
                    asyncOptions: asyncCategories({
                      t,
                      dealService,
                      productService,
                      farmId,
                      classifierKeys: [
                        Classifier.PREPACKAGES_FOR_VETERINARY_MEDICINES,
                      ],
                    }),
                    label: 'pages.warehouse.deals.packaging_type',
                    cacheOn: ['packaging_type_id'],
                    requiredShow: true,
                  },
                  true
                )
              : null,

            additionalParametersKeys.includes(exceptionProducts) &&
            dealType === DealType.purchased
              ? Watch(
                  Amount(
                    { amount: 'packaging_size', unit: 'packaging_unit' },
                    {
                      asyncOptions: async (
                        value,
                        loadedOptions,
                        { parentName, getValues }
                      ) => {
                        return {
                          hasMore: false,
                          options:
                            getValues(
                              parentName
                                ? parentName.concat('.product_type_id')
                                : 'product_type_id'
                            )?.units?.map((v: string) => ({
                              label: v,
                              value: v,
                            })) ?? [],
                          additional: {},
                        };
                      },
                      cacheOn: ['product_type_id.value'],
                      label: 'pages.warehouse.deals.packaging_size',
                      onDisabled: 'both',
                    },
                    true,
                    true
                  ),
                  ({ value: [size], parentName, getValues, setValue }) => {
                    handlePackagingSize(
                      'packaging_size',
                      size,
                      parentName,
                      setValue,
                      getValues,
                      calculationValues
                    );

                    handlePackagingUnit(parentName, setValue, getValues);
                  }
                )
              : null,
            additionalParametersKeys.includes(exceptionProducts) &&
            dealType === DealType.purchased
              ? Watch(
                  Decimal(
                    'packaging_amount',
                    'pages.warehouse.deals.packaging_amount',
                    null,
                    null,
                    true,
                    null,
                    true
                  ),
                  ({ value, parentName, setValue, getValues }) => {
                    handlePackagingAmount(
                      'packaging_amount',
                      value,
                      parentName,
                      setValue,
                      getValues,
                      calculationValues
                    );
                  }
                )
              : null,
            additionalParametersKeys.includes(exceptionProducts) &&
            dealType === DealType.purchased
              ? Date(
                  'expires_at',
                  'pages.warehouse.deals.expires_at',
                  null,
                  null,
                  true
                )
              : null,

            Watch(
              Amount(
                { amount: 'amount', unit: 'unit' },
                {
                  asyncOptions: async (
                    value,
                    loadedOptions,
                    { parentName, getValues }
                  ) => {
                    return {
                      hasMore: false,
                      options:
                        getValues(
                          parentName
                            ? parentName.concat('.product_type_id')
                            : 'product_type_id'
                        )?.units?.map((v: string) => ({
                          label: v,
                          value: v,
                        })) ?? [],
                      additional: {},
                    };
                  },
                  cacheOn: ['product_type_id.value'],
                  label: 'pages.warehouse.deals.amount',
                  onDisabled: 'both',
                  disabled:
                    additionalParametersKeys.includes(exceptionProducts) &&
                    dealType === DealType.purchased,
                },
                true
              ),
              ({ value: [amount], parentName, getValues, setValue }) => {
                if (
                  newPartyNumberList.includes(dealType) ||
                  (DealType.lost === dealType &&
                    getValuesMainForm('subtype') === DealType.lost)
                ) {
                  setDealProductPartyNumbers(
                    farmId,
                    dealType,
                    dealService,
                    setValue,
                    getValues,
                    originalFormValues
                  );
                }

                handleAmount(
                  'amount',
                  amount,
                  parentName,
                  setValue,
                  getValues,
                  calculationValues
                );
              }
            ),
            (!newPartyNumberList.includes(dealType) &&
              !(
                DealType.lost === dealType &&
                getValuesMainForm('subtype') === DealType.lost
              )) ||
            newWarehouseList.includes(dealType)
              ? Group(
                  [
                    !newPartyNumberList.includes(dealType) &&
                    !(
                      DealType.lost === dealType &&
                      getValuesMainForm('subtype') === DealType.lost
                    )
                      ? warehouse(
                          warehouseService,
                          farmId,
                          false,
                          true,
                          'product_warehouses.0.warehouse_id'
                        )
                      : null,
                    newWarehouseList.includes(dealType) ||
                    (DealType.lost === dealType &&
                      getValuesMainForm('subtype') === DealType.leftover)
                      ? makeButton(
                          'pages.warehouse.deals.new_warehouse',
                          css['new_warehouse'],
                          '',
                          faPlus,
                          undefined,
                          () => setOpenModal(true)
                        )
                      : null,
                  ],
                  null,
                  null,
                  false,
                  true,
                  `${css['new_warehouse_box']} 'no-border-bottom'`
                )
              : null,
          ].filter(Boolean),
          null,
          null,
          false,
          true,
          `${css['deal_form_top']} no-border-bottom`
        ),
        Group(
          [
            newPartyNumberList.includes(dealType) ||
            (DealType.lost === dealType &&
              getValuesMainForm('subtype') === DealType.lost)
              ? renderWarehousePartyNumber()
              : partyNumber(true),
            dealType === DealType.lost
              ? Text(
                  'reason',
                  'pages.warehouse.deals.reason',
                  undefined,
                  { requiredShow: exceptionProducts === 'MEDICATION' },
                  true
                )
              : null,
            Watch(
              Decimal(
                'price',
                'pages.warehouse.deals.price_unit',
                null,
                null,
                true
              ),
              ({ value, parentName, setValue, getValues }) => {
                return handlePrice(
                  'price',
                  value,
                  parentName,
                  setValue,
                  getValues,
                  calculationValues
                );
              }
            ),
            Watch(
              Decimal('sum', 'pages.warehouse.deals.sum', null, null, true),
              ({ value, parentName, setValue, getValues }) => {
                if (useCalculator) {
                  return handleSum(
                    'sum',
                    value,
                    parentName,
                    setValue,
                    getValues,
                    calculationValues
                  );
                }

                const vat_rate = getValues(
                  parentName ? parentName.concat('.vat_rate') : 'vat_rate'
                );

                if (value && vat_rate) {
                  setValue(
                    parentName ? parentName.concat('.vat') : 'vat',
                    toFixed(parseFloat(value) * parseFloat(vat_rate.rate), 2)
                  );
                }
              }
            ),
          ].filter(Boolean),
          null,
          null,
          false,
          true,
          'no-border-bottom'
        ),
        farm?.vat
          ? Group([
              Watch(
                Select(
                  'vat_rate',
                  {
                    asyncOptions: (value, loadedOptions, { page }) =>
                      classifierService
                        .values(
                          ClassifierGroup.WAREHOUSE,
                          Classifier.VAT_RATES,
                          {
                            value,
                          }
                        )
                        .pipe(
                          map((response: ClassifierValue[]) => ({
                            hasMore: false,
                            options: response.map((v) => ({
                              label: v.value,
                              value: v.id,
                              rate: v.value,
                            })),
                            additional: {},
                          }))
                        ),
                    label: 'pages.warehouse.deals.vat_rate',
                  },
                  true
                ),
                ({ value, parentName, setValue, getValues }) => {
                  if (useCalculator) {
                    return handleVatRate(
                      'vat_rate',
                      value,
                      parentName,
                      setValue,
                      getValues,
                      calculationValues
                    );
                  }

                  value = Array.isArray(value) ? value[0] : value;
                  const sum = getValues(
                    parentName ? parentName.concat('.sum') : 'sum'
                  );

                  if (sum && value?.rate) {
                    const vat = toFixed(
                      parseFloat(sum) * (parseFloat(value.rate) / 100),
                      2
                    );
                    setValue(
                      parentName ? parentName.concat('.vat') : 'vat',
                      vat
                    );

                    setValue(
                      parentName
                        ? parentName.concat('.sum_with_vat')
                        : 'sum_with_vat',
                      toFixed(parseFloat(sum) + parseFloat(vat), 2)
                    );
                  }
                }
              ),
              Watch(
                Decimal('vat', 'pages.warehouse.deals.vat', null, null, true),
                ({ value, parentName, setValue, getValues }) => {
                  return handleVat(
                    'vat',
                    value,
                    parentName,
                    setValue,
                    getValues,
                    calculationValues
                  );
                }
              ),
              Watch(
                Decimal(
                  'sum_with_vat',
                  'pages.warehouse.deals.sum_with_vat',
                  null,
                  null,
                  true
                ),
                ({ value, parentName, setValue, getValues }) => {
                  return handleSumWithVat(
                    'sum_with_vat',
                    value,
                    parentName,
                    setValue,
                    getValues,
                    calculationValues
                  );
                }
              ),
            ])
          : Group([]),
      ].filter(Boolean),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [exceptionProducts, warehouseId]
  );
}

export const dealSchema = (
  productObtainTypeRequired: boolean = false,
  packagingRequired: boolean = false,
  packagingTypeRequired: boolean = false,
  medicationRequired: boolean = false,
) => {
  return yup.object().shape({
    product_obtain_type_id: productObtainTypeRequired
      ? yup.string().required()
      : yup.string().nullable(),
    product_category_id: yup.object().shape({
      value: yup.string().required(),
    }),
    product_type_id: yup.object().shape({
      value: yup.string().required(),
    }),
    product_id: yup.object().shape({
      value: yup.string().required(),
    }),
    packaging_type_id: yup.object().shape({
      value: packagingRequired && packagingTypeRequired
        ? yup.string().required()
        : yup.string().nullable(),
    }),
    packaging_size: packagingRequired
      ? yup.number().positive().decimal(8, 3).required()
      : yup.string().nullable(),
    packaging_amount: packagingRequired
      ? yup.number().positive().decimal(8, 3).required()
      : yup.string().nullable(),
    amount: yup.number().positive().decimal(8, 3).required(),
    unit: yup.object().shape({
      value: yup.string().required(),
    }),
    reason: medicationRequired
      ? yup.string().required()
      : yup.string().nullable(),
    product_warehouses: yup
      .array()
      .of(
        yup.object().shape({
          warehouse_id: yup.object().required().shape({
            value: yup.string().required(),
          }),
        })
      )
      .min(1),
  });
};
